import { Operation } from "../../sync-engine/operations/Operation";
import { getTrackedRange, removeTrackedRange, isRangePersistent } from "../TrackedRangeStore";

interface SetSnippetParams {
  ooxml: string;
  trackedRangeId: string;
}

/**
 * Word SyncEngine Operation for setting OOXML on a tracked document range.
 *
 * Requires a trackedRangeId to find the tracked range created by GetSnippetOperation.
 * Replaces the tracked range with the provided OOXML.
 *
 * Throws an error if the tracked range is not found - there is no fallback
 * to the current selection to avoid inserting content in the wrong location.
 */
export class SetSnippetOperation implements Operation<void> {
  private readonly params: SetSnippetParams;

  constructor(params: SetSnippetParams) {
    this.params = params;
  }

  async commit(): Promise<void> {
    const { ooxml, trackedRangeId } = this.params;

    // Get the tracked range from the store
    const trackedRange = getTrackedRange(trackedRangeId);
    if (!trackedRange) {
      throw new Error(
        `Cannot find the original document location. The range "${trackedRangeId}" is no longer tracked. ` +
          "This can happen if the document was closed, the add-in was reloaded, or too much time has passed. " +
          "Please re-select the text you want to modify and try again.",
      );
    }

    // Use Word.run with the tracked range to ensure it's valid in a new context
    await Word.run(trackedRange, async (context) => {
      trackedRange.insertOoxml(ooxml, "Replace");
      await context.sync();
    });

    // Only clean up non-persistent ranges after replacement.
    // Persistent ranges (from word_add_in_query_range) should survive for
    // potential subsequent edits. They'll be evicted by LRU or time expiry.
    if (!isRangePersistent(trackedRangeId)) {
      removeTrackedRange(trackedRangeId);
    }
  }

  /**
   * SetSnippetOperation does not merge with other operations.
   */
  merge(_other: Operation<unknown>): Operation<void> | undefined {
    return undefined;
  }
}
